/**
 * 帮助弹窗展示管理工具
 * 用于管理页面帮助弹窗的展示规则：首次自动弹出，点击"不再弹出"后7天内不再弹出，7天后重新触发
 * 
 * 支持账号隔离：不同账号的弹窗状态独立存储
 */

import { Storage } from './storage'
import store from '@/store'

// 存储键前缀
const HELP_DIALOG_PREFIX = 'HELP_DIALOG_'

// 7天的毫秒数
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

/**
 * 获取当前用户ID
 * @returns {string} 用户ID，如果获取失败返回空字符串
 */
const getCurrentUserId = () => {
  try {
    const userInfo = store?.state?.user?.userInfo
    if (!userInfo) return ''
    
    // 支持多种用户ID字段名
    const userId = userInfo.id || userInfo.userID || userInfo.userId || userInfo.guid
    
    if (!userId) return ''
    
    // 转换为字符串并去除空格
    return String(userId).trim()
  } catch (e) {
    console.warn('获取当前用户ID失败:', e)
    return ''
  }
}

/**
 * 生成存储键（带账号隔离）
 * @param {string} pageKey - 页面唯一标识（如 'FOLLOW', 'SENDING'）
 * @returns {string} 完整的存储键，格式：HELP_DIALOG_{userId}_{pageKey}_CLOSE_TIME
 */
const getStorageKey = (pageKey) => {
  const userId = getCurrentUserId()
  
  if (!userId) {
    // 如果获取不到用户ID，使用通用键（向后兼容）
    console.warn('无法获取用户ID，使用通用存储键')
    return `${HELP_DIALOG_PREFIX}${pageKey}_CLOSE_TIME`
  }
  
  // 带用户ID的存储键，实现账号隔离
  return `${HELP_DIALOG_PREFIX}${userId}_${pageKey}_CLOSE_TIME`
}

// 导出页面键常量，方便统一管理
export const PAGE_KEYS = {
  FOLLOW: 'FOLLOW',
  SENDING: 'SENDING'
}

/**
 * 模块/入口开关配置项
 * 暂时隐藏某个帮助模块时置为 false；恢复展示时改回 true 即可，无需改动业务代码
 * key 对应 PAGE_KEYS
 */
const HELP_DIALOG_ENABLED_CONFIG = {
  [PAGE_KEYS.FOLLOW]: false,   // 客户跟进-功能说明帮助模块：暂时隐藏
  [PAGE_KEYS.SENDING]: false   // 群发营销-功能说明帮助模块：暂时隐藏
}

/**
 * 判断指定页面的帮助模块/入口是否启用（供页面控制问号入口与弹窗的显隐）
 * 未配置的页面默认启用，避免影响后续新增页面
 * @param {string} pageKey - 页面唯一标识
 * @returns {boolean}
 */
export const isHelpDialogEnabled = (pageKey) => HELP_DIALOG_ENABLED_CONFIG[pageKey] !== false

/**
 * 动态设置指定页面的帮助模块开关（用于测试或运行时临时调整；长期开关请改 HELP_DIALOG_ENABLED_CONFIG）
 * @param {string} pageKey - 页面唯一标识
 * @param {boolean} enabled - 是否启用
 */
export const setHelpDialogEnabled = (pageKey, enabled) => {
  HELP_DIALOG_ENABLED_CONFIG[pageKey] = enabled
}

/**
 * 判断是否应该展示帮助弹窗
 * @param {string} pageKey - 页面唯一标识（如 'FOLLOW', 'SENDING'）
 * @returns {boolean} true表示应该展示，false表示不应该展示
 */
export const shouldShowHelpDialog = (pageKey) => {
  // 模块被配置项隐藏时，不自动弹出
  if (!isHelpDialogEnabled(pageKey)) {
    return false
  }

  const storageKey = getStorageKey(pageKey)
  const closeTime = Storage.get(storageKey)
  
  // 首次进入（没有关闭记录）
  if (!closeTime) {
    return true
  }
  
  // 判断是否超过7天
  const now = Date.now()
  const timeDiff = now - closeTime
  
  // 超过7天，重新展示
  if (timeDiff >= SEVEN_DAYS_MS) {
    return true
  }
  
  // 7天内不展示
  return false
}

/**
 * 记录用户点击"不再弹出"的时间
 * @param {string} pageKey - 页面唯一标识（如 'FOLLOW', 'SENDING'）
 */
export const markHelpDialogClosed = (pageKey) => {
  const storageKey = getStorageKey(pageKey)
  const now = Date.now()
  Storage.set(storageKey, now)
}

/**
 * 清除特定页面的关闭记录（用于测试或重置）
 * @param {string} pageKey - 页面唯一标识
 */
export const clearHelpDialogRecord = (pageKey) => {
  const storageKey = getStorageKey(pageKey)
  Storage.remove(storageKey)
}

/**
 * 获取距离下次展示还剩多少天（用于调试）
 * @param {string} pageKey - 页面唯一标识
 * @returns {number|null} 剩余天数，如果没有记录则返回null
 */
export const getDaysUntilNextShow = (pageKey) => {
  const storageKey = getStorageKey(pageKey)
  const closeTime = Storage.get(storageKey)
  
  if (!closeTime) {
    return null
  }
  
  const now = Date.now()
  const timeDiff = now - closeTime
  const remainingMs = SEVEN_DAYS_MS - timeDiff
  
  if (remainingMs <= 0) {
    return 0
  }
  
  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000))
}
